<template>
  <standard-dialog
    :value="value"
    :title="doc ? `${documentTypeLabel(doc.document_type)} #${doc.number || doc.id}` : ''"
    icon="mdi-file-document-outline"
    @input="$emit('input', $event)"
    hide-actions
  >
    <template v-if="doc">
      <div class="d-flex justify-end mb-3">
        <excel-button class="mr-2" @click="exportExcel" />
        <print-button @click="print" />
      </div>
      <v-simple-table class="mb-4">
        <tbody>
          <tr>
            <td class="font-weight-bold">{{ $t("date") }}</td>
            <td>{{ localeDate(doc.document_date) }}</td>
          </tr>
          <tr>
            <td class="font-weight-bold">{{ $t("status") }}</td>
            <td>
              <v-chip small :color="getStatusColor(doc.status)" dark>
                {{ $t(statusKeyMap[doc.status] || doc.status) }}
              </v-chip>
            </td>
          </tr>
          <tr v-if="doc.offer_deadline">
            <td class="font-weight-bold">{{ $t("warehouse.offers_deadline") }}</td>
            <td>{{ localeDate(doc.offer_deadline) }}</td>
          </tr>
          <tr v-if="doc.delivery_deadline">
            <td class="font-weight-bold">{{ $t("warehouse.delivery_deadline") }}</td>
            <td>{{ localeDate(doc.delivery_deadline) }}</td>
          </tr>
          <tr v-if="doc.notes">
            <td class="font-weight-bold">{{ $t("note") }}</td>
            <td>{{ toLatin(doc.notes) }}</td>
          </tr>
        </tbody>
      </v-simple-table>

      <div class="subtitle-2 mb-2">{{ $t("routes.namirnice") }}</div>
      <v-data-table
        :headers="groceryHeaders"
        :items="doc.items || []"
        hide-default-footer
        :items-per-page="-1"
        class="striped-table elevation-0"
        dense
      >
        <template v-slot:item.grocery_name="{ item }">
          {{ item.grocery ? toLatin(item.grocery.name) : "—" }}
        </template>
        <template v-slot:item.grocery_unit="{ item }">
          {{ item.grocery ? item.grocery.unit : "—" }}
        </template>
      </v-data-table>
    </template>
  </standard-dialog>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import warehouseFiltersMixin from "@/mixins/warehouse/warehouseFiltersMixin";
import hasPrintingV2 from "@/mixins/hasPrintingV2";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "SupplierPortalDetailDialog",
  mixins: [warehouseFiltersMixin, hasPrintingV2, hasExcel],
  components: { StandardDialog },
  props: {
    value: { type: Boolean, default: false },
    doc: { type: Object, default: null },
  },
  computed: {
    documentTypes() {
      return {
        order: this.$t("warehouse.purchase_order"),
        entry: this.$t("warehouse.warehouse_replenishment"),
        request: this.$t("warehouse.request_quote"),
      };
    },
    groceryHeaders() {
      return [
        { text: this.$t("title"), value: "grocery_name" },
        { text: this.$t("q"), value: "amount" },
        { text: this.$t("unit_of_measurment"), value: "grocery_unit" },
        { text: this.$t("price"), value: "unit_price" },
      ];
    },
  },
  methods: {
    documentTypeLabel(type) {
      return this.documentTypes[type] || type;
    },
    print() {
      if (!this.doc) return;
      const { row, col, text, bold, table, spacer, label } = this.printElements;
      const docType = this.documentTypeLabel(this.doc.document_type);
      const docNumber = this.doc.number || this.doc.id;
      const fmt = (d) => this.localeDate(d) || "—";
      const items = (this.doc.items || []).map((item, i) => ({
        num: i + 1,
        grocery_name: item.grocery
          ? this.toLatin(item.grocery.name)
          : `#${item.grocery_id}`,
        amount: item.amount,
        grocery_unit: item.grocery?.unit || "—",
        unit_price: item.unit_price ?? "—",
      }));
      const printHeaders = [
        { text: "#", value: "num", width: "50px" },
        { text: this.$t("title"), value: "grocery_name" },
        { text: this.$t("q"), value: "amount", align: "right" },
        { text: this.$t("unit_of_measurment"), value: "grocery_unit" },
        { text: this.$t("price"), value: "unit_price", align: "right" },
      ];
      this.printDoc(
        [
          row("between align-end", [
            col([
              bold(`${docType} #${docNumber}`, 18),
              spacer(8),
              label(this.$t("date") + ":", fmt(this.doc.document_date)),
              this.doc.offer_deadline
                ? label(
                    this.$t("warehouse.offers_deadline") + ":",
                    fmt(this.doc.offer_deadline)
                  )
                : "",
              this.doc.delivery_deadline
                ? label(
                    this.$t("warehouse.delivery_deadline") + ":",
                    fmt(this.doc.delivery_deadline)
                  )
                : "",
              this.doc.notes
                ? label(this.$t("note") + ":", this.toLatin(this.doc.notes))
                : "",
            ]),
            col("end", [
              label(
                this.$t("status") + ":",
                this.$t(this.statusKeyMap[this.doc.status] || this.doc.status)
              ),
            ]),
          ]),
          spacer(16),
          table(printHeaders, items),
        ],
        { pageTitle: `${docType} #${docNumber}` }
      );
    },
    async exportExcel() {
      if (!this.doc) return;
      const docType = this.documentTypeLabel(this.doc.document_type);
      const docNumber = this.doc.number || this.doc.id;
      const items = (this.doc.items || []).map((item, i) => ({
        num: i + 1,
        grocery_name: item.grocery
          ? this.toLatin(item.grocery.name)
          : `#${item.grocery_id}`,
        amount: item.amount,
        grocery_unit: item.grocery?.unit || "—",
        unit_price: item.unit_price ?? "—",
      }));
      await this.downloadExcel({
        fileName: `${docType}-${docNumber}.xlsx`,
        sheets: [
          {
            name: `${docType} #${docNumber}`.substring(0, 31),
            title: `${docType} #${docNumber}`,
            subtitle: this.localeDate(this.doc.document_date),
            headers: [
              { text: "#", value: "num" },
              { text: this.$t("title"), value: "grocery_name" },
              { text: this.$t("q"), value: "amount" },
              { text: this.$t("unit_of_measurment"), value: "grocery_unit" },
              { text: this.$t("price"), value: "unit_price" },
            ],
            items,
          },
        ],
      });
    },
  },
};
</script>
